import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useEmailTemplatePreviewQuery, useEmailTemplatesQuery } from "../hooks/useEmailTemplates";
import type { EmailTemplate } from "../types";

const EmailTemplatesPage = () => {
  const { t, i18n } = useTranslation("common");
  const { data: templates, isLoading, isFetching } = useEmailTemplatesQuery();
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [languageFilter, setLanguageFilter] = useState(() => {
    const lang = i18n.language;
    if (lang.startsWith("pt")) return "pt";
    if (lang === "es" || lang === "en") return lang;
    return "all";
  });
  const [keyFilter, setKeyFilter] = useState("all");

  const { data: previewHtml, isLoading: isPreviewLoading } = useEmailTemplatePreviewQuery(
    selectedTemplate?.id ?? null
  );

  const isBusy = isLoading || isFetching;

  const uniqueKeys = useMemo(
    () => [...new Set((templates ?? []).map((t) => t.key))].sort(),
    [templates]
  );

  const filteredTemplates = useMemo(() => {
    let result = templates ?? [];
    if (languageFilter !== "all") {
      result = result.filter((t) => t.language === languageFilter);
    }
    if (keyFilter !== "all") {
      result = result.filter((t) => t.key === keyFilter);
    }
    return result;
  }, [templates, languageFilter, keyFilter]);

  const handleRowClick = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewOpen(true);
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title={t("EMAIL_TEMPLATES.title")} subtitle={t("EMAIL_TEMPLATES.subtitle")} />

      <div className="flex items-center gap-4">
        <Select value={languageFilter} onValueChange={setLanguageFilter}>
          <SelectTrigger className="w-[180px] border-neutral-700 bg-neutral-800/50 text-neutral-100">
            <SelectValue placeholder={t("EMAIL_TEMPLATES.allLanguages")} />
          </SelectTrigger>
          <SelectContent className="border-neutral-700 bg-neutral-800 text-neutral-100">
            <SelectItem value="all">{t("EMAIL_TEMPLATES.allLanguages")}</SelectItem>
            <SelectItem value="en">EN</SelectItem>
            <SelectItem value="es">ES</SelectItem>
            <SelectItem value="pt">PT</SelectItem>
          </SelectContent>
        </Select>

        <Select value={keyFilter} onValueChange={setKeyFilter}>
          <SelectTrigger className="w-[220px] border-neutral-700 bg-neutral-800/50 text-neutral-100">
            <SelectValue placeholder={t("EMAIL_TEMPLATES.allKeys")} />
          </SelectTrigger>
          <SelectContent className="border-neutral-700 bg-neutral-800 text-neutral-100">
            <SelectItem value="all">{t("EMAIL_TEMPLATES.allKeys")}</SelectItem>
            {uniqueKeys.map((key) => (
              <SelectItem key={key} value={key}>
                {key}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-neutral-800 bg-neutral-900/50">
        <Table>
          <TableHeader>
            <TableRow className="border-neutral-800 hover:bg-transparent">
              <TableHead className="text-neutral-400">{t("EMAIL_TEMPLATES.name")}</TableHead>
              <TableHead className="text-neutral-400">{t("EMAIL_TEMPLATES.key")}</TableHead>
              <TableHead className="text-neutral-400">{t("EMAIL_TEMPLATES.language")}</TableHead>
              <TableHead className="text-neutral-400">{t("EMAIL_TEMPLATES.subject")}</TableHead>
              <TableHead className="text-neutral-400">{t("EMAIL_TEMPLATES.updated")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredTemplates.map((template) => (
              <TableRow
                key={template.id}
                className="border-neutral-800 hover:bg-neutral-800/50 cursor-pointer"
                onClick={() => handleRowClick(template)}
              >
                <TableCell className="font-medium text-neutral-100">{template.name}</TableCell>
                <TableCell>
                  <code className="rounded bg-neutral-800 px-1.5 py-0.5 text-xs text-neutral-300">
                    {template.key}
                  </code>
                </TableCell>
                <TableCell>
                  <Badge
                    className={
                      template.language === "es"
                        ? "bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"
                        : template.language === "pt"
                          ? "bg-green-500/20 text-green-400 hover:bg-green-500/30"
                          : "bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                    }
                  >
                    {template.language.toUpperCase()}
                  </Badge>
                </TableCell>
                <TableCell className="text-neutral-200 max-w-[300px] truncate">
                  {template.subject}
                </TableCell>
                <TableCell className="text-sm text-neutral-300">
                  {formatDistanceToNow(new Date(template.updatedAt), {
                    addSuffix: true,
                    locale: es,
                  })}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {isBusy && <LoadingOverlay />}

        {!isLoading && filteredTemplates.length === 0 && (
          <EmptyState
            title={t("EMAIL_TEMPLATES.emptyTitle")}
            description={t("EMAIL_TEMPLATES.emptyDescription")}
            className="m-6"
          />
        )}

        <div className="border-t border-neutral-800 px-4 py-3 text-xs text-neutral-400">
          {t("EMAIL_TEMPLATES.showing")} {filteredTemplates.length} {t("EMAIL_TEMPLATES.of")}{" "}
          {templates?.length ?? 0} templates
        </div>
      </Card>

      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent className="border-neutral-800 bg-neutral-900 text-neutral-100 max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{t("EMAIL_TEMPLATES.previewTitle")}</DialogTitle>
            <DialogDescription className="text-neutral-400">
              {selectedTemplate?.name} ({selectedTemplate?.key})
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 rounded-lg border border-neutral-700 overflow-hidden">
            {isPreviewLoading ? (
              <div className="flex items-center justify-center h-[500px]">
                <svg className="h-6 w-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            ) : (
              <iframe
                srcDoc={previewHtml || ""}
                title="Email Preview"
                className="w-full h-[600px] bg-white"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailTemplatesPage;
